import { useState } from 'react';
import { Box, Container, Grid, IconButton, Stack, Typography } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

export default function FeatureSlider() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const features = [
    {
      title: 'Transform Your Models',
      description:
        'Transform your finite element meshes to perfectly match target surfaces from CAD, structured light scans, or other FEMs, ensuring precision and consistency.',
      image: '/features/thumbnails/as-manufactured-rotor-deviation.webp',
    },
    {
      title: 'Support for Cyclic Features',
      description:
        'Efficiently handle full rotors or individual sectors, empowering accurate simulations for critical rotating machinery.',
      image: '/features/thumbnails/rotor_scan.webp',
    },
    {
      title: 'Boolean Operations',
      description:
        'Perform complex geometric edits like cutting holes, notches, and other features directly into mesh surfaces with unparalleled accuracy.',
      image: '/features/thumbnails/after_cut.webp',
    },
    {
      title: 'Scripting',
      description:
        'Record workflows and generate reusable, standalone scripts to automate repetitive tasks and streamline your processes.',
      image: '/features/thumbnails/scripting.webp',
    },
    {
      title: 'CAD Integrations',
      description:
        'Import and morph meshes to STEP and IGES models seamlessly, bridging the gap between CAD design and analysis workflows.',
      image: '/features/thumbnails/nist-cad.webp',
    },
    {
      title: 'Robust Automated Alignment',
      description:
        'Achieve fully automated and highly accurate part registration using full or partial feature recognition, ensuring optimal alignment in any workflow.',
      image: '/features/thumbnails/align_example_initial.webp',
    },
  ];

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? features.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === features.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        overflow: 'hidden',
        py: { xs: 7, md: 10 },
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="xl">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'flex-end' }} justifyContent="space-between" sx={{ mb: 4 }}>
          <Box>
            <Typography variant="overline" color="secondary.dark">Core capabilities</Typography>
            <Typography variant="h3" sx={{ mt: 1 }}>Built for repeatable engineering workflows.</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={handlePrev} aria-label="Previous feature">
              <ArrowBack />
            </IconButton>
            <IconButton onClick={handleNext} aria-label="Next feature">
              <ArrowForward />
            </IconButton>
          </Stack>
        </Stack>
        <Box
          sx={{
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              transform: `translateX(-${currentIndex * 100}%)`,
              transition: 'transform 0.45s ease',
            }}
          >
            {features.map((feature, index) => (
              <Box key={feature.title} sx={{ flex: '0 0 100%' }}>
                <Grid container>
                  <Grid item xs={12} md={7}>
                    <Box
                      component="img"
                      src={feature.image}
                      alt={feature.title}
                      loading={index === 0 ? undefined : 'lazy'}
                      sx={{
                        width: '100%',
                        height: { xs: 280, sm: 380, md: 520 },
                        display: 'block',
                        objectFit: 'cover',
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <Stack sx={{ height: '100%', p: { xs: 3, md: 5 }, justifyContent: 'center' }}>
                      <Typography variant="overline" color="text.secondary">
                        {String(index + 1).padStart(2, '0')} / {String(features.length).padStart(2, '0')}
                      </Typography>
                      <Typography variant="h4" sx={{ my: 2 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75 }}>
                        {feature.description}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} aria-label="Feature position">
          {features.map((feature, index) => (
            <Box
              key={feature.title}
              component="button"
              type="button"
              aria-label={`Show ${feature.title}`}
              onClick={() => setCurrentIndex(index)}
              sx={{
                width: index === currentIndex ? 34 : 10,
                height: 10,
                border: 0,
                borderRadius: 999,
                bgcolor: index === currentIndex ? 'primary.main' : 'divider',
                cursor: 'pointer',
                transition: 'width 0.2s ease',
              }}
            />
          ))}
        </Stack>
      </Container>
    </Box>

  );
}
